'use client';

import { useEffect, useRef } from 'react';
import { useRouter } from 'next/navigation';
import {
  DialogBase,
  Drawer,
  ButtonBase,
  HTMLRenderer,
} from '@bts-web/core-features/common';
import { useClientTranslation } from '@bts-web/utils-lokalise';
import { css, stack } from '@bts-web/utils-style-engine';
import { getAppConfig } from '@bts-web/core-app-config';
import { NEXT_ROUTES } from '@bts-web/utils-next-infrastructure';
import { useDrawerMetadata } from '../../../../common/components/ModalDrawerContext/ModalDrawerContext';
import { useAppNotification } from '../../../../notifications/NotificationContextProvider';
import {
  getCoolOffPeriodTranslations,
  CoolOffPeriodTranslations,
} from './translations';
import { TimerMode } from './Timer';
import {
  InfoContentLayout,
  bodyTextStyles,
  bodyGroupStyles,
} from './InfoContentLayout';

export type InfoViewMode = TimerMode | 'tradeWaitingPeriod';

const WELCOME_TOAST_KEY = 'welcomeToastShown';

const InitialAlmostThereContent = ({
  translations,
  onClose,
}: {
  translations: CoolOffPeriodTranslations;
  onClose?: () => void;
}) => (
  <InfoContentLayout
    title={translations.almostThereTitle}
    actions={
      <ButtonBase visual="primary" size="large" fullWidth onClick={onClose}>
        {translations.gotItButton}
      </ButtonBase>
    }
  >
    <div className={bodyGroupStyles}>
      <p>{translations.initialCheckingDetails}</p>
      <p>
        <HTMLRenderer>{translations.initialRegulationText}</HTMLRenderer>
      </p>
      <p>{translations.initialExploreText}</p>
    </div>
  </InfoContentLayout>
);

const CoolOffContent = ({
  translations,
  onClose,
}: {
  translations: CoolOffPeriodTranslations;
  onClose?: () => void;
}) => {
  const router = useRouter();

  const { bitpandaAcademyPageUrl } = getAppConfig();

  const handleGoToQuiz = () => {
    onClose?.();

    router.push(
      `${NEXT_ROUTES.APPROPRIATENESS_QUESTIONS_INTRO}?next=portfolio`,
    );
  };

  const handleGoToAcademy = () => {
    if (bitpandaAcademyPageUrl) {
      window.open(bitpandaAcademyPageUrl, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <InfoContentLayout
      title={translations.coolingOffPeriodTitle}
      actions={
        <div className={stack({ gap: 'small', width: '100%' })}>
          <ButtonBase
            visual="primary"
            size="large"
            fullWidth
            onClick={handleGoToQuiz}
          >
            {translations.goToFinancialQuizButton}
          </ButtonBase>
          <ButtonBase
            visual="secondary"
            size="large"
            fullWidth
            onClick={handleGoToAcademy}
          >
            {translations.goToBitpandaAcademyButton}
          </ButtonBase>
        </div>
      }
    >
      <div className={bodyGroupStyles}>
        <p>{translations.coolingOffPeriodDescription1}</p>
        <p>{translations.coolingOffPeriodDescription2}</p>
      </div>
    </InfoContentLayout>
  );
};

const AlmostThereContent = ({
  translations,
  onClose,
}: {
  translations: CoolOffPeriodTranslations;
  onClose?: () => void;
}) => (
  <InfoContentLayout
    title={translations.almostThereTitle}
    actions={
      <ButtonBase visual="primary" size="large" fullWidth onClick={onClose}>
        {translations.gotItButton}
      </ButtonBase>
    }
  >
    <p className={bodyTextStyles}>{translations.almostThereDescription}</p>
  </InfoContentLayout>
);

const WaitingPeriodContent = ({
  translations,
}: {
  translations: CoolOffPeriodTranslations;
}) => (
  <InfoContentLayout
    title={translations.waitingPeriodTitle}
    actions={
      <div className={stack({ gap: 'small', width: '100%' })}>
        <ButtonBase visual="secondary" size="large" fullWidth>
          {translations.goToAcademyButton}
        </ButtonBase>
        <ButtonBase visual="primary" size="large" fullWidth disabled>
          {translations.retryFinancialQuizButton}
        </ButtonBase>
      </div>
    }
  >
    <div className={bodyGroupStyles}>
      <p>{translations.waitingPeriodDescription1}</p>
      <p>{translations.waitingPeriodDescription2}</p>
    </div>
  </InfoContentLayout>
);

const TradeWaitingPeriodContent = ({
  translations,
  onClose,
}: {
  translations: CoolOffPeriodTranslations;
  onClose?: () => void;
}) => (
  <InfoContentLayout
    title={translations.almostThereTitle}
    actions={
      <ButtonBase visual="primary" size="large" fullWidth onClick={onClose}>
        {translations.gotItButton}
      </ButtonBase>
    }
  >
    <p className={bodyTextStyles}>
      {translations.tradeWaitingPeriodDescription}
    </p>
  </InfoContentLayout>
);

export type InfoViewVariant = 'initial' | 'default';

interface InfoViewProps {
  isMobile: boolean | null;
  onClose?: () => void;
  mode?: InfoViewMode;
  isAACompleted?: boolean;
  variant?: InfoViewVariant;
}

export const InfoView = ({
  isMobile,
  onClose,
  mode = 'coolOff',
  isAACompleted = false,
  variant = 'default',
}: InfoViewProps) => {
  const { t } = useClientTranslation();

  const { setAppNotification } = useAppNotification();

  const { closeThisDrawer } = useDrawerMetadata();

  const translations = getCoolOffPeriodTranslations(t);

  const close = onClose ?? closeThisDrawer;

  // Stable ref so the cleanup always has the latest setAppNotification/t
  const notificationRef = useRef({ setAppNotification, t });

  notificationRef.current = { setAppNotification, t };

  // Fire welcome toast when the initial variant unmounts (any dismissal method).
  // The deferred flag skips React Strict Mode's synchronous unmount/remount cycle.
  useEffect(() => {
    if (variant !== 'initial') return;

    let isActive = false;

    const frameId = requestAnimationFrame(() => {
      isActive = true;
    });

    return () => {
      cancelAnimationFrame(frameId);

      if (!isActive) return;

      const { setAppNotification: notify, t: translate } =
        notificationRef.current;

      try {
        localStorage.setItem(WELCOME_TOAST_KEY, '1');
      } catch {
        // localStorage unavailable
      }

      notify({
        title: translate('welcome_to_bitpanda_title'),
        subtitle: translate('welcome_to_bitpanda_description'),
        visual: 'success',
      });
    };
  }, [variant]);

  const renderContent = () => {
    if (mode === 'tradeWaitingPeriod') {
      return (
        <TradeWaitingPeriodContent
          translations={translations}
          onClose={close}
        />
      );
    }

    if (mode === 'waitingPeriod') {
      return <WaitingPeriodContent translations={translations} />;
    }

    if (variant === 'initial') {
      return (
        <InitialAlmostThereContent
          translations={translations}
          onClose={close}
        />
      );
    }

    if (isAACompleted) {
      return <AlmostThereContent translations={translations} onClose={close} />;
    }

    return <CoolOffContent translations={translations} onClose={close} />;
  };

  // Only show drawer header title for waiting period modes
  const drawerTitle =
    mode === 'waitingPeriod' || mode === 'tradeWaitingPeriod'
      ? translations.waitingPeriodTitle
      : undefined;

  if (isMobile) {
    return (
      <Drawer title={drawerTitle}>
        <div
          className={css({
            padding: 'medium',
          })}
        >
          {renderContent()}
        </div>
      </Drawer>
    );
  }

  return (
    <DialogBase
      id="cool-off-period-modal"
      size="small"
      onMobileFullScreen
      visual="secondary"
      title={drawerTitle}
      smallTitle
      onClose={close}
    >
      {renderContent()}
    </DialogBase>
  );
};
